<!--
	PS-6495 — the guided setup, inside the dialog that frames the workflow.

	Flow: intro → sign in → enter your own number → confirm the terms and book. The number is
	settled BEFORE the order so the order summary can state what it will actually signal, instead of
	carrying a "decided later" placeholder into a contract screen.

	The consent block sits ON the order screen, above the button that concludes the contract, as the
	prototype has it. Its German is verbatim legal text (`sip_consent_agb`, `sip_consent_waiver_tkg71`)
	and arrives through the translations like every other string here.

	D6 condition: no wording below may claim the number is already being signalled or verified -
	except the overview after a VERIFIED result, the one moment it is true.

	H4 (PS-6495 code review 2): `sip_booked_display_number_*` names the entered number as the
	displayed one only after a VERIFIED result now; unproven, the screens name the SIP
	number. Still open: `sip_number_hint` and `sip_order_signalled_number_label` speak of the entered
	number as the signalled one BEFORE it is proven - copy question for the PO. Marked "H4" below.

	Failure paths (developer decision 2026-10-12): `unconfirmed` and `serviceFollowUp` end with the
	contact callback (`contactService`), on their close button and on the header's close. `rejected`
	lets the customer choose (2026-10-16): "Service soll sich bei mir melden" creates it, "Jetzt nicht"
	and the header's close end the run without a callback.

	Buttons are plain elements rather than Material's: in a dialog footer the stock chrome fought the
	rest of the page, and `.button-base`'s fixed 200 px belongs to the page's action bar, not here.

	Link entry (`entry === 'link'`): no rail, close, sign-in or number step; an empty header or
	footer is hidden rather than left as a bordered strip.
-->
<div (keydown)="onUserActivity()" (pointerdown)="onUserActivity()" [class.setup--page]="isLinkEntry" class="setup" data-testid="guided-setup">
	<header [hidden]="!hasHeaderContent" class="setup__head" data-testid="guided-setup-header">
		<!--
			Hidden once the run has ended in failure. No step is current there, so the counter fell back
			to zero and read "step 0 of 4" on a screen that is not step zero and has no next step.
			Progress through a flow that has stopped is not information.
		-->
		@if (showsProgress) {
			<!--
				L14: named by the progress line beside it - the rail and the counter say the same thing,
				and no dedicated i18n key for the rail exists.
			-->
			<ol aria-labelledby="guided-setup-progress" class="setup__rail" data-testid="guided-setup-rail">
				@for (step of store.displaySteps(); track step) {
					<li
						[attr.aria-current]="isStepCurrent(step) ? 'step' : null"
						[class.done]="isStepDone(step)"
						[class.now]="isStepCurrent(step)"
						[title]="stepLabel(step) | i18n | async"
					>
						<span class="dot">
							@if (isStepDone(step)) {
								<mat-icon aria-hidden="true" class="material-symbols-outlined">check</mat-icon>
							} @else {
								<!-- Zero-based: the intro is step 0 because it informs rather than asks. -->
								{{ $index }}
							}
						</span>
					</li>
				}
			</ol>
			<!--
				`aria-live` because nothing else announces a step change: the rail's `aria-current` moves
				silently, and on a narrow viewport the rail is not rendered at all, which used to leave
				no step information in the accessibility tree whatsoever. Polite, so it waits rather
				than interrupting whatever the customer is reading.
			-->
			<span
				aria-live="polite"
				class="setup__progress"
				data-testid="guided-setup-progress"
				id="guided-setup-progress"
			>
				{{ progressLabel }}
			</span>
		}
		<!--
			Who the run is signed in as belongs here, not above the step's heading. It applies to the
			whole run rather than to this screen, and in the body it sat in an accent colour above the
			h2 and read as the most important thing there (Torsten, 2026-09-11). As header metadata it
			confirms the login without competing with it.
		-->
		@if (store.signedInAs() && store.phase() !== 'intro' && store.phase() !== 'signIn') {
			<!--
				The name in its OWN span. On the flex container the `text-overflow: ellipsis` never
				applied - the name was an anonymous flex item rather than an inline box, so a long one
				was hard-clipped mid-character instead of ellipsised. The title attribute carries the
				full name either way.
			-->
			<span class="setup__signed-in" data-testid="guided-setup-signed-in" [title]="signedInAsLabel">
				<mat-icon aria-hidden="true" class="material-symbols-outlined">check_circle</mat-icon>
				<span class="setup__signed-in-name">{{ store.signedInAs() }}</span>
			</span>
		}
		<!-- Suspends the run, except on the screens that end with the service (see `closeFromHeader`). -->
		@if (!isLinkEntry) {
			<button
				(click)="closeFromHeader()"
				[disabled]="closeDisabled"
				[attr.aria-label]="I18N.SIP_CLOSE_SETUP | i18n | async"
				class="setup__close"
				data-testid="guided-setup-close"
				type="button"
			>
				<mat-icon aria-hidden="true" class="material-symbols-outlined">close</mat-icon>
			</button>
		}
	</header>

	<!--
		Every phase's heading carries `#stepHeading` and `tabindex="-1"` (H6: focus moves to it on each
		phase change) and the id `guided-setup-heading`, which names the dialog (M8: `ariaLabelledBy`
		in the dialog config). Only one phase renders at a time, so the id stays unique.
	-->
	<div class="setup__body" data-testid="guided-setup-body">
		@switch (store.phase()) {
			@case ('resolving') {
				<!-- No words: the translations arrive with the offer. -->
				<div aria-busy="true" class="setup__resolving" data-testid="guided-setup-resolving" role="status">
					<span aria-hidden="true" class="setup__spinner"></span>
				</div>
			}

			@case ('linkExpired') {
				<!-- EXPIRED and INVALID share one informational screen (BR5): the customer did nothing wrong. -->
				<h2
					#stepHeading
					class="setup__title setup__title--info"
					data-testid="guided-setup-link-expired"
					id="guided-setup-heading"
					tabindex="-1"
				>
					<mat-icon aria-hidden="true" class="material-symbols-outlined">link_off</mat-icon>
					{{ I18N.SIP_LINK_EXPIRED_TITLE | i18n | async }}
				</h2>
				<p class="setup__lead">{{ I18N.SIP_LINK_EXPIRED_LEAD | i18n | async }}</p>
			}

			@case ('linkNotOrderable') {
				<!-- The cause stays out of the copy: the customer cannot fix it. -->
				<h2
					#stepHeading
					class="setup__title setup__title--info"
					data-testid="guided-setup-link-not-orderable"
					id="guided-setup-heading"
					tabindex="-1"
				>
					<mat-icon aria-hidden="true" class="material-symbols-outlined">support_agent</mat-icon>
					{{ I18N.SIP_LINK_NOT_ORDERABLE_TITLE | i18n | async }}
				</h2>
				<p class="setup__lead">{{ I18N.SIP_LINK_NOT_ORDERABLE_LEAD | i18n | async }}</p>
			}

			@case ('intro') {
				<h2 #stepHeading data-testid="guided-setup-intro" id="guided-setup-heading" tabindex="-1">{{ I18N.SIP_INTRO_TITLE | i18n | async }}</h2>
				<p class="setup__lead">{{ introLead | i18n | async }}</p>
				<!--
					Two different runs, two different promises. With a booking ahead, the list says what
					the customer gets; without one, listing a SIP-Anschluss and a contract extension would
					promise things this run does not do (Torsten, 2026-09-11) — there nothing is booked
					and nothing is charged, and saying so plainly is the whole message.

					The lead-in above the list is load-bearing: without it three bullets follow a sentence
					about what is MISSING and read as more of the same.
				-->
				@if (booksSipAccount) {
					<p class="setup__lead-in">{{ I18N.SIP_INTRO_INCLUDED_LEAD | i18n | async }}</p>
					<ul class="setup__list">
						@for (item of includedItems; track item) {
							<li>{{ item | i18n | async }}</li>
						}
					</ul>
				} @else {
					<p class="setup__lead">{{ I18N.SIP_INTRO_NO_COST_NOTE | i18n | async }}</p>
				}
			}

			@case ('signIn') {
				<h2 #stepHeading data-testid="guided-setup-sign-in" id="guided-setup-heading" tabindex="-1">{{ I18N.SIP_SIGN_IN_TITLE | i18n | async }}</h2>
				<p class="setup__lead">{{ I18N.SIP_SIGN_IN_LEAD | i18n | async }}</p>

				<!--
					The form lives here rather than in a popup or on the authorization server's page.
					The customer never leaves the dialog, so the half-written callback behind it cannot
					be lost and no popup blocker or in-app webview can intercept the flow.
				-->
				<!--
					L11: a real form, so Enter submits and password managers recognise the pair. Its submit
					button sits in the footer and joins through `form="guided-setup-sign-in-form"`;
					`submitSignIn` cancels the native submission and applies the button's condition.

					L10: a refused sign-in marks both credentials invalid, a wrong code the code field; every
					field points at the error line while there is one.
				-->
				<form
					(submit)="submitSignIn($event)"
					data-testid="guided-setup-sign-in-form"
					id="guided-setup-sign-in-form"
					novalidate
				>
					<div class="field">
						<label for="setup-username">{{ I18N.SIP_SIGN_IN_USERNAME | i18n | async }}</label>
						<input
							(input)="username = $any($event.target).value"
							[attr.aria-describedby]="store.loginError() ? 'setup-login-error' : null"
							[attr.aria-invalid]="store.loginError() === I18N.SIP_LOGIN_REJECTED ? 'true' : null"
							[value]="username"
							autocomplete="username"
							data-testid="guided-setup-username"
							id="setup-username"
							type="text"
						/>
					</div>

					<div class="field">
						<label for="setup-password">{{ I18N.SIP_SIGN_IN_PASSWORD | i18n | async }}</label>
						<input
							(input)="password = $any($event.target).value"
							[attr.aria-describedby]="store.loginError() ? 'setup-login-error' : null"
							[attr.aria-invalid]="store.loginError() === I18N.SIP_LOGIN_REJECTED ? 'true' : null"
							[value]="password"
							autocomplete="current-password"
							data-testid="guided-setup-password"
							id="setup-password"
							type="password"
						/>
					</div>

					<!--
						Only after the server has asked for it. The account's credentials were already
						accepted at that point, so this is a second step and not an error.
					-->
					@if (store.twoFactorRequired()) {
						<!--
							One block, not a loose line above a loose field (Torsten, 2026-09-11). The
							explanation and the input are one thought: this account needs a second factor,
							here is where it goes.
						-->
						<div class="field two-factor">
							<p class="two-factor__hint" id="setup-2fa-hint">
								{{ I18N.SIP_LOGIN_TWO_FACTOR_HINT | i18n | async }}
							</p>
							<label for="setup-2fa">{{ I18N.SIP_SIGN_IN_TWO_FACTOR_CODE | i18n | async }}</label>
							<input
								(input)="twoFactorCode = $any($event.target).value"
								[attr.aria-describedby]="store.loginError() ? 'setup-2fa-hint setup-login-error' : 'setup-2fa-hint'"
								[attr.aria-invalid]="store.loginError() === I18N.SIP_LOGIN_TWO_FACTOR_WRONG ? 'true' : null"
								[value]="twoFactorCode"
								autocomplete="one-time-code"
								data-testid="guided-setup-two-factor"
								id="setup-2fa"
								inputmode="numeric"
								type="text"
							/>
						</div>
					}
				</form>

				@if (store.loginError(); as loginError) {
					<!-- An error, not a caution: the sign-in did not happen. `--warn` read as advice. -->
					<p
						class="setup__note setup__note--error"
						data-testid="guided-setup-login-error"
						id="setup-login-error"
						role="alert"
					>
						{{ loginError | i18n | async }}
					</p>
				}
			}

			@case ('chooseNumber') {
				<h2 #stepHeading data-testid="guided-setup-choose-number" id="guided-setup-heading" tabindex="-1">{{ I18N.SIP_NUMBER_TITLE | i18n | async }}</h2>
				@if (availableNumbers.length) {
					<p class="setup__lead">{{ I18N.SIP_NUMBER_LEAD_CHOICES | i18n | async }}</p>
					<!--
						Both paths on screen at once. The candidates used to appear only on focus, so a
						customer who never clicked the field never learned there was a choice. Rendered
						exactly as delivered (D8): the backend owns this list and its validation, and
						PS-6450 forbids a second copy here.
					-->
					<!--
						Buttons, not click-handlers on <li>. As bare list items these rows were reachable
						only with a mouse: no tabindex, no role, no key handling — so a keyboard or
						screen-reader customer could not complete this step at all, and the continue button stays
						disabled until one is chosen. `aria-pressed` carries the selected state that the
						`.picked` class only shows.
					-->
					<ul class="choices">
						@for (candidate of availableNumbers; track candidate.number) {
							<li [class.picked]="isNumberPicked(candidate.number)">
								<button
									(click)="pickNumber(candidate.number)"
									[attr.aria-pressed]="isNumberPicked(candidate.number)"
									data-testid="guided-setup-number-option"
									type="button"
								>
									<span class="tick"></span>
									<span class="num">{{ candidate.number }}</span>
									<span class="name">{{ candidate.name }}</span>
								</button>
							</li>
						}
						<!--
							Bound to showCustomInput, not to the mode: the field also opens for a configured
							number that matches no row, and a row that is visibly in use must read as picked.
						-->
						<li [class.picked]="showCustomInput">
							<button
								(click)="useCustomNumber()"
								[attr.aria-pressed]="showCustomInput"
								data-testid="guided-setup-number-option-other"
								type="button"
							>
								<span class="tick"></span>
								<span class="num">{{ I18N.SIP_NUMBER_OPTION_OTHER | i18n | async }}</span>
							</button>
						</li>
					</ul>
				} @else {
					<!--
						No candidates at all — the common case, since the backend usually delivers an empty
						list. The field is then the only way in, so it carries the explanation that the list
						would otherwise have given.
					-->
					<p class="setup__lead">{{ I18N.SIP_NUMBER_LEAD_NO_CHOICES | i18n | async }}</p>
				}

				@if (showCustomInput) {
					<div class="field">
						<label for="setup-number">{{ I18N.SIP_NUMBER_FIELD | i18n | async }}</label>
						<!--
							L9: the format error waits until the field has been left once (`numberTouched`),
							so its alert does not fire on the first keystroke. L10: the field is invalid while
							that error shows and is described by whichever lines are on screen.
						-->
						<input
							(blur)="numberTouched = true"
							(input)="onNumberInput($any($event.target).value)"
							[attr.aria-describedby]="
								(numberIsPreconfigured ? 'setup-number-known ' : '') +
								(visibleNumberError ? 'setup-number-error' : 'setup-number-hint') +
								(store.numberError() ? ' setup-number-verdict' : '')
							"
							[attr.aria-invalid]="visibleNumberError || store.numberError() ? 'true' : null"
							[value]="store.outgoingNumber()"
							#numberInput
							autocomplete="tel"
							data-testid="guided-setup-number"
							id="setup-number"
							inputmode="tel"
							[placeholder]="(I18N.SIP_NUMBER_PLACEHOLDER | i18n | async) ?? ''"
							type="tel"
						/>
						<!--
							Confirms what the customer is looking at: this number is already set up, the step
							is asking them to check it rather than supply it (Torsten, 2026-09-11). Reuses the
							header's confirmation styling instead of inventing a second one.
						-->
						@if (numberIsPreconfigured) {
							<p class="setup__note setup__note--known" id="setup-number-known">
								<mat-icon aria-hidden="true" class="material-symbols-outlined">check_circle</mat-icon>
								{{ I18N.SIP_NUMBER_ALREADY_STORED | i18n | async }}
							</p>
						}
						<!-- One secondary line at a time: the error replaces the hint rather than stacking under it. -->
						<!-- H4: `sip_number_hint` names the entered number as the signalled one - see the file header. -->
						@if (visibleNumberError; as formatError) {
							<p class="field__error" data-testid="guided-setup-number-error" id="setup-number-error" role="alert">
								{{ formatError | i18n | async }}
							</p>
						} @else {
							<p class="field__hint" data-testid="guided-setup-number-hint" id="setup-number-hint">
								{{ I18N.SIP_NUMBER_HINT | i18n | async }}
							</p>
						}
					</div>
				}
				<!--
					The § 120 TKG declaration, beside the number it is about (Torsten, 2026-10-14) and
					only when a verification follows. Its displayed text is what the trigger sends and the
					backend keeps. Wording approved 2026-10-16.
				-->
				@if (numberAsksDeclaration) {
					<label [class.is-missing]="declarationMissing" class="check">
						<input
							(change)="toggleDeclaration()"
							[attr.aria-describedby]="declarationMissing ? 'setup-declaration-error' : null"
							[attr.aria-invalid]="declarationMissing ? 'true' : null"
							[checked]="store.declarationAccepted()"
							data-testid="guided-setup-number-declaration"
							type="checkbox"
						/>
						<span class="legal">
							<span>{{ declarationText }}</span>
							<span aria-hidden="true" class="req">*</span>
						</span>
					</label>
					@if (declarationMissing) {
						<p class="setup__note setup__note--error" data-testid="guided-setup-declaration-error" id="setup-declaration-error" role="alert">
							{{ I18N.SIP_ERROR_CONSENT_DECLARATION | i18n | async }}
						</p>
					}
				}
				<!-- The pre-check's or phoneservice's verdict on the number, for a picked row as much as a typed one. -->
				@if (store.numberError(); as numberError) {
					<p class="setup__note setup__note--error" data-testid="guided-setup-number-verdict" id="setup-number-verdict" role="alert">
						{{ numberError | i18n | async }}
					</p>
				}
			}

			@case ('order') {
				<h2 #stepHeading data-testid="guided-setup-order" id="guided-setup-heading" tabindex="-1">{{ I18N.SIP_ORDER_TITLE | i18n | async }}</h2>
				<!--
					No benefit paragraph here. The customer decided two steps ago; on the screen where the
					order is placed it is noise, and it was also the 26 px that pushed this panel into a
					scrollbar (measured 2026-09-11).
				-->
				<!--
					The order step in two halves, so a viewport that is wide but SHORT can set them side by
					side instead of stacking them. Both wrappers are `display: contents` by default, so on a
					viewport that does NOT get the two-column treatment they generate no boxes at all and the
					layout is exactly what it was before they existed. See `.order__grid` in the SCSS.
				-->
				<div class="order__grid" data-testid="guided-setup-order-grid">
					<!-- What is being committed to, as one object. -->
					<section class="summary" data-testid="guided-setup-order-summary">
						<dl>
							<!-- First, so the recipient of a forwarded link notices it is not theirs. -->
							@if (isLinkEntry && store.companyName(); as company) {
								<div data-testid="guided-setup-order-company">
									<dt>{{ I18N.CORRECT_COMPANY | i18n | async }}</dt>
									<dd>{{ company }}</dd>
								</div>
							}
							<div>
								<dt>{{ I18N.SIP_ORDER_TARIFF_LABEL | i18n | async }}</dt>
								<dd>{{ I18N.SIP_ORDER_TARIFF_NAME | i18n | async }}</dd>
							</div>
							<!--
								The provider's name links to the brand's homepage row; the documents named in
								the consent below link to their own rows (`LegalLinksService`). A homepage is
								never used as a stand-in for a document - that would claim the document is
								there when it is not.

								`noopener noreferrer` because the customer is mid-order: the new tab must not
								reach back into this one, and the order screen's URL is nobody else's business.
							-->
							<div>
								<dt>{{ I18N.SIP_ORDER_PROVIDER_LABEL | i18n | async }}</dt>
								<!--
									M15: the brand's `egroup_homepage` row (LegalLinksService), like the consent
									links; without one the name is plain text rather than a guessed URL.
								-->
								<dd data-testid="guided-setup-provider">
									@if (providerUrl; as url) {
										<a
											[href]="url"
											class="link link--external"
											data-testid="guided-setup-provider-link"
											rel="noopener noreferrer"
											target="_blank"
										>
											{{ I18N.SIP_ORDER_PROVIDER_NAME | i18n | async }}
											<mat-icon aria-hidden="true" class="material-symbols-outlined">open_in_new</mat-icon>
										</a>
									} @else {
										{{ I18N.SIP_ORDER_PROVIDER_NAME | i18n | async }}
									}
								</dd>
							</div>
							<!-- H4: labels the entered number as the signalled one - see the file header. -->
							@if (!isLinkEntry) {
								<div>
									<dt>{{ I18N.SIP_ORDER_SIGNALLED_NUMBER_LABEL | i18n | async }}</dt>
									<dd data-testid="guided-setup-order-number">{{ signalledNumber }}</dd>
								</div>
							}
							<div>
								<dt>{{ I18N.SIP_ORDER_MONTHLY_COST_LABEL | i18n | async }}</dt>
								<dd>
									<!--
										Never invent a figure on the screen where a contract is concluded — and since
										PS-6526 there are two different reasons for not having one. A single "not
										decided yet" line used to cover both and is now false in each: the price IS
										decided, it is either not offered on this plan or could not be read.
									-->
									@if (monthlyCost) {
										{{ monthlyCost }}
									} @else if (!sipOnOffer) {
										<span class="summary__open">{{ I18N.SIP_ORDER_PRICE_NOT_IN_TARIFF | i18n | async }}</span>
									} @else {
										<span class="summary__open">{{ I18N.SIP_ORDER_PRICE_UNAVAILABLE | i18n | async }}</span>
									}
								</dd>
							</div>
						</dl>
						<p class="summary__title">{{ I18N.SIP_ORDER_INCLUDED_TITLE | i18n | async }}</p>
						<ul class="setup__list">
							@for (item of includedItems; track item) {
								<li>{{ item | i18n | async }}</li>
							}
						</ul>
					</section>
					<!--
						The consent half. The error note lives in here too: it has to sit beside the fields it
						names, never in the other column.
					-->
					<div class="order__consent" data-testid="guided-setup-order-consent">
						<!--
							Two required boxes, both carrying LINKS inside the label. That is deliberate and it is
							safe: the HTML spec exempts interactive descendants from a label's activation behaviour,
							so clicking the terms link opens the document without also ticking the
							box the customer was only trying to read. Verified in a browser rather than trusted -
							silently toggling a consent box while someone reads the terms would be the worst possible
							place for that to be wrong.

							`[innerHTML]` because the links live inside the sentence and the sentence is legal text
							that may not be cut into fragments to accommodate markup. The sentence comes from the
							database, so Angular's sanitizer running on it is wanted.
						-->
						<label [class.is-missing]="termsMissing" class="check">
							<input
								(change)="toggleTerms()"
								[attr.aria-invalid]="termsMissing ? 'true' : null"
								[checked]="store.termsAccepted()"
								data-testid="guided-setup-consent-terms"
type="checkbox"
							/>
							<span class="legal">
								<span [innerHTML]="agbText"></span>
								<span aria-hidden="true" class="req">*</span>
							</span>
						</label>

						<!--
							The § 71 Abs. 3 TKG declaration, final wording (Torsten, 2026-10-09). One required
							checkbox again, replacing the two-statement radio choice of 2026-10-07 and the full
							declaration that sat behind a disclosure - the new sentence carries its own condition,
							so the customer it does not apply to declares nothing by ticking it.
						-->
						<label [class.is-missing]="waiverMissing" class="check">
							<input
								(change)="toggleWaiver()"
								[attr.aria-invalid]="waiverMissing ? 'true' : null"
								[checked]="store.waiverAccepted()"
								data-testid="guided-setup-consent-waiver"
type="checkbox"
							/>
							<span class="legal">
								<span [innerHTML]="waiverText"></span>
								<span aria-hidden="true" class="req">*</span>
							</span>
						</label>

						<!-- The key to the `*` markers above, which is the only place it means anything. -->
						<p class="consent-required-note" data-testid="guided-setup-consent-required-note">{{ I18N.SIP_CONSENT_REQUIRED_NOTE | i18n | async }}</p>

						@if (store.consentError(); as consentError) {
							<p class="setup__note setup__note--error" data-testid="guided-setup-consent-error" role="alert">
								{{ consentError | i18n | async }}
							</p>
						}
					</div>
				</div>
			}

			@case ('verify') {
				<h2 #stepHeading data-testid="guided-setup-verify" id="guided-setup-heading" tabindex="-1">{{ I18N.SIP_VERIFY_TITLE | i18n | async }}</h2>
				<p class="setup__lead">{{ I18N.SIP_VERIFY_LEAD | i18n | async }}</p>
				<!-- The number in full and large: the one thing to check before a phone rings. -->
				<div class="headline-number">
					<p class="headline-number__label">{{ I18N.SIP_VERIFY_NUMBER_LABEL | i18n | async }}</p>
					<p class="headline-number__value" data-testid="guided-setup-verify-number">{{ signalledNumber }}</p>
					<p class="headline-number__note">{{ I18N.SIP_VERIFY_NUMBER_QUESTION | i18n | async }}</p>
				</div>
				<p class="setup__muted">{{ I18N.SIP_VERIFY_SIP_NUMBER_NOTE | i18n | async }}</p>
			}

			@case ('verifyRunning') {
				<h2 #stepHeading data-testid="guided-setup-verify-running" id="guided-setup-heading" tabindex="-1">{{ I18N.SIP_VERIFY_RUNNING_TITLE | i18n | async }}</h2>
				<!--
					The code is the dominant element (AC: large, high contrast, selectable). It comes only
					from the trigger answer and lives on the component-scoped controller - never in the store.
				-->
				<div class="verify-code">
					<p class="verify-code__label" id="setup-verify-code-label">{{ I18N.SIP_VERIFY_CODE_LABEL | i18n | async }}</p>
					<!-- The large digits are for the eye; screen readers get them one by one, after the label. -->
					<p aria-hidden="true" class="verify-code__digits" data-testid="guided-setup-verify-code">{{ verification.code() }}</p>
					<p class="verify-code__spoken" data-testid="guided-setup-verify-code-spoken">{{ verifyCodeSpoken }}</p>
				</div>
				<!-- Announced when it changes (calling → answered); the countdown below is not, it would talk every second. -->
				<p aria-live="polite" class="verify-status" data-testid="guided-setup-verify-status" role="status">
					<span aria-hidden="true" class="verify-status__pulse"></span>
					{{ verifyCallingLabel }}
				</p>
				@if (verifyValidForLabel; as validFor) {
					<p class="setup__muted" data-testid="guided-setup-verify-valid-for">{{ validFor }}</p>
				}
			}

			@case ('verified') {
				<!--
					A moment, not an overview: centred, no box, so it does not read as a preview of step 5
					(Torsten, 2026-10-14). The overview that follows carries the box and the actions.
				-->
				<div class="verified-state">
					<mat-icon aria-hidden="true" class="material-symbols-outlined verified-state__icon">check_circle</mat-icon>
					<h2 #stepHeading class="verified-state__title" data-testid="guided-setup-verified" id="guided-setup-heading" tabindex="-1">
						{{ I18N.SIP_VERIFY_SUCCESS_TITLE | i18n | async }}
					</h2>
					<p class="verified-state__number" data-testid="guided-setup-verified-number">{{ signalledNumber }}</p>
					<p class="setup__muted">{{ I18N.SIP_BOOKED_DISPLAY_NUMBER_NOTE | i18n | async }}</p>
				</div>
			}

			@case ('verifyResult') {
				<!-- "Not yet", not an error: the heading carries a neutral marker, never the failure red. -->
				<h2
					#stepHeading
					class="setup__title setup__title--pending"
					data-testid="guided-setup-verify-result"
					id="guided-setup-heading"
					tabindex="-1"
				>
					<mat-icon aria-hidden="true" class="material-symbols-outlined">info</mat-icon>
					{{ verifyResultCopy.title | i18n | async }}
				</h2>
				<p class="setup__lead" data-testid="guided-setup-verify-result-lead">{{ verifyResultLead }}</p>
				@if (verifyResultIsLocked) {
					@if (store.manualRequestSent()) {
						<p class="setup__note setup__note--info" data-testid="guided-setup-verify-manual-sent" role="status">
							{{ I18N.SIP_VERIFY_MANUAL_SENT | i18n | async }}
						</p>
					} @else {
						<div class="field">
							<label for="setup-verify-note">{{ I18N.SIP_VERIFY_MANUAL_NOTE_LABEL | i18n | async }}</label>
							<textarea
								(input)="manualNote = $any($event.target).value"
								[maxLength]="manualNoteMaxLength"
								[value]="manualNote"
								data-testid="guided-setup-verify-note"
								id="setup-verify-note"
								rows="3"
							></textarea>
						</div>
						@if (store.manualRequestFailed()) {
							<p class="setup__note setup__note--error" data-testid="guided-setup-verify-manual-failed" role="alert">
								{{ I18N.SIP_VERIFY_FAILED_LEAD | i18n | async }}
							</p>
						}
					}
				}
				<p class="setup__note setup__note--info" data-testid="guided-setup-verify-sip-number">{{ I18N.SIP_VERIFY_SIP_NUMBER_NOTE | i18n | async }}</p>
			}

			@case ('booked') {
				<h2 #stepHeading data-testid="guided-setup-booked" id="guided-setup-heading" tabindex="-1">{{ successTitle | i18n | async }}</h2>
				<!--
					TWO numbers, and they are not the same one. The booked SIP number is what the backend
					assigned; the displayed number is the one the customer entered and the person being
					called will see. Until 2026-09-11 this screen printed the booked number as the one
					that would be displayed, which was simply wrong.
				-->
				<!--
					Not two equal rows. The displayed number is what the customer actually cares about —
					it is what the person being called will see — while the booked SIP number is the
					technical result of the order, worth stating for the record and no more. Torsten,
					2026-09-11. Equal weighting made the reader work out which one mattered.
				-->
				@if (isLinkEntry) {
					<!-- A link reopened after the order (offer COMPLETED) has no booked number to show. -->
					@if (store.bookedNumber(); as booked) {
						<dl class="result">
							<div>
								<dt>{{ I18N.SIP_BOOKED_SIP_NUMBER_LABEL | i18n | async }}</dt>
								<dd data-testid="guided-setup-booked-number">{{ booked }}</dd>
							</div>
						</dl>
					}
				} @else {
					<!--
						The entered number is named as the displayed one ONLY once it is proven (H4, resolved by
						the verification). Unproven, the screen names what the called party actually sees: the booked 032
						number, or - on a run without a booking, where the page does not know it - says so.
					-->
					<!-- One box for the number that matters; the booking is a quiet line under it (Torsten, 2026-10-14). -->
					<p class="setup__lead">{{ I18N.SIP_BOOKED_CALLBACK_PENDING | i18n | async }}</p>
					@if (store.numberVerified()) {
						<div class="headline-number">
							<p class="headline-number__label">{{ I18N.SIP_BOOKED_DISPLAY_NUMBER_LABEL | i18n | async }}</p>
							<p class="headline-number__value" data-testid="guided-setup-display-number">
								{{ signalledNumber }}
								<span class="verified-chip" data-testid="guided-setup-verified-chip">
									<mat-icon aria-hidden="true" class="material-symbols-outlined">check_circle</mat-icon>
									{{ I18N.SIP_VERIFY_VERIFIED_CHIP | i18n | async }}
								</span>
							</p>
						</div>
						@if (store.bookedNumber(); as bookedNumber) {
							<p class="setup__muted setup__muted--small" data-testid="guided-setup-booked-number">
								{{ I18N.SIP_BOOKED_SIP_NUMBER_LABEL | i18n | async }}: {{ bookedNumber }}
							</p>
						}
					} @else if (store.bookedNumber(); as bookedNumber) {
						<div class="headline-number">
							<p class="headline-number__label">{{ I18N.SIP_BOOKED_UNVERIFIED_LABEL | i18n | async }}</p>
							<p class="headline-number__value" data-testid="guided-setup-booked-number">{{ bookedNumber }}</p>
							<p class="headline-number__note">{{ I18N.SIP_VERIFY_SIP_NUMBER_NOTE | i18n | async }}</p>
						</div>
					} @else {
						<p class="setup__note setup__note--info" data-testid="guided-setup-booked-sip-number">{{ I18N.SIP_VERIFY_SIP_NUMBER_NOTE | i18n | async }}</p>
					}
				}
			}

			@case ('rejected') {
				<!--
					A failure has to be legible as one before it is read (Torsten, 2026-09-11). The colour
					carries the state; the text stays calm, because the customer did nothing wrong and a
					screen shouting in red would suggest they did.
				-->
				<h2
					#stepHeading
					class="setup__title setup__title--error"
					data-testid="guided-setup-rejected"
					id="guided-setup-heading"
					tabindex="-1"
				>
					<mat-icon aria-hidden="true" class="material-symbols-outlined">error</mat-icon>
					{{ I18N.SIP_REJECTED_TITLE | i18n | async }}
				</h2>
				<p class="setup__lead">{{ I18N.SIP_REJECTED_LEAD | i18n | async }}</p>
				<!--
					H2 revised (Torsten, 2026-10-16): closing used to create the contact callback blindly.
					The customer now chooses in the footer; the follow-up line is gone because it promised
					a callback that only exists after "Service soll sich bei mir melden".
				-->
			}

			@case ('unconfirmed') {
				<!--
					M4: the order timed out or answered 504, so nobody knows whether it was booked. Same
					heading treatment as the other result screens; no retry anywhere on it, because a second
					order could book a second billed account.
				-->
				<h2
					#stepHeading
					class="setup__title setup__title--error"
					data-testid="guided-setup-unconfirmed"
					id="guided-setup-heading"
					tabindex="-1"
				>
					<mat-icon aria-hidden="true" class="material-symbols-outlined">error</mat-icon>
					{{ I18N.SIP_ORDER_UNCONFIRMED_TITLE | i18n | async }}
				</h2>
				<p class="setup__lead">{{ I18N.SIP_ORDER_UNCONFIRMED_LEAD | i18n | async }}</p>
				<p class="setup__note setup__note--info" data-testid="guided-setup-unconfirmed-follow-up" role="status">
					{{ I18N.SIP_CALLBACK_SERVICE_FOLLOW_UP | i18n | async }}
				</p>
			}

			@case ('addressInvalid') {
				<!--
					PS-6605: the contract address was unusable, nothing was booked. Same heading treatment as
					the other result screens; no retry (a second order cannot fix the address) and no extra
					follow-up note - the lead already says the service gets in touch, and
					SIP_CALLBACK_SERVICE_FOLLOW_UP talks about a callback "to set up the number".
				-->
				<h2
					#stepHeading
					class="setup__title setup__title--error"
					data-testid="guided-setup-address-invalid"
					id="guided-setup-heading"
					tabindex="-1"
				>
					<mat-icon aria-hidden="true" class="material-symbols-outlined">error</mat-icon>
					{{ I18N.SIP_ADDRESS_INVALID_TITLE | i18n | async }}
				</h2>
				<p class="setup__lead" role="status">{{ I18N.SIP_ADDRESS_INVALID_LEAD | i18n | async }}</p>
			}

			@case ('serviceFollowUp') {
				<!--
					M5: the customer asked the service to contact them. No title key exists for this
					screen, so the heading repeats the button they pressed - copy is never composed here.
				-->
				<h2
					#stepHeading
					data-testid="guided-setup-service-follow-up"
					id="guided-setup-heading"
					tabindex="-1"
				>{{ I18N.SIP_BTN_CONTACT_SERVICE | i18n | async }}</h2>
				<p class="setup__lead" role="status">{{ I18N.SIP_CALLBACK_SERVICE_FOLLOW_UP | i18n | async }}</p>
			}

			@case ('failed') {
				<h2
					#stepHeading
					class="setup__title setup__title--error"
					data-testid="guided-setup-failed"
					id="guided-setup-heading"
					tabindex="-1"
				>
					<mat-icon aria-hidden="true" class="material-symbols-outlined">error</mat-icon>
					<!-- A link order may have booked despite the error, so its title must not say it failed. -->
					{{ (isLinkEntry ? I18N.SIP_ORDER_UNCONFIRMED_TITLE : I18N.SIP_FAILED_TITLE) | i18n | async }}
				</h2>
				<!--
					No "we will get in touch" on this screen itself: closing it creates no task. The
					promise is made only once the customer chooses `sip_btn_contact_service` (not offered in
					the special case), which creates the contact callback - see `serviceFollowUp`.
				-->
				@if (isLinkEntry) {
					<!-- After a timeout or 504 the order may have gone through, so this must not claim it failed. -->
					<p class="setup__lead" data-testid="guided-setup-link-order-failed">
						{{ I18N.SIP_LINK_ORDER_FAILED_LEAD | i18n | async }}
					</p>
				} @else {
					<p class="setup__lead">{{ I18N.SIP_FAILED_LEAD | i18n | async }}</p>
					<p class="setup__lead">{{ I18N.SIP_CONTACT_SERVICE | i18n | async }}</p>
					<p class="setup__note setup__note--error" role="status">
						{{ I18N.SIP_CALLBACK_NOT_ORDERED | i18n | async }}
					</p>
				}
			}
		}
	</div>

	<!--
		Actions in a footer, primary on the right, the way a dialog is read. "Not now" and "Cancel"
		sit left because leaving is not the thing being offered. Both END the run; only the header
		close and "Later" on the confirmation keep it (a run that has booked must stay resumable).
	-->
	<footer
		[class.setup__foot--link]="isLinkEntry"
		[hidden]="!hasFooterActions"
		class="setup__foot"
		data-testid="guided-setup-footer"
	>
		@switch (hasFooterActions ? store.phase() : null) {
			@case ('intro') {
				<button (click)="dismiss()" class="btn btn--text" data-testid="guided-setup-not-now" type="button">
					{{ I18N.SIP_BTN_NOT_NOW | i18n | async }}
				</button>
				<button (click)="start()" class="btn btn--brand" data-testid="guided-setup-start" type="button">
					{{ I18N.SIP_BTN_START | i18n | async }}
				</button>
			}
			@case ('signIn') {
				<button
					(click)="dismiss()"
					[disabled]="store.busy()"
					class="btn btn--text"
					data-testid="guided-setup-cancel"
					type="button"
				>
					{{ I18N.SIP_BTN_CANCEL | i18n | async }}
				</button>
				@if (offersContactService) {
					<button
						(click)="requestServiceContact()"
						class="btn btn--quiet"
						data-testid="guided-setup-contact-service"
						type="button"
					>
						{{ I18N.SIP_BTN_CONTACT_SERVICE | i18n | async }}
					</button>
				}
				<!-- Submits the sign-in form above (L11); `submitSignIn` runs on that form's submit. -->
				<button
					[disabled]="!canSignIn"
					class="btn btn--primary"
					data-testid="guided-setup-sign-in-submit"
					form="guided-setup-sign-in-form"
					type="submit"
				>
					{{ (store.busy() ? I18N.SIP_BTN_SIGN_IN_BUSY : I18N.SIP_BTN_SIGN_IN) | i18n | async }}
				</button>
			}
			@case ('chooseNumber') {
				<button (click)="dismiss()" class="btn btn--text" data-testid="guided-setup-cancel" type="button">
					{{ I18N.SIP_BTN_CANCEL | i18n | async }}
				</button>
				<!-- A refused number is never a dead end (AC7, D19): go on without verification, the SIP number is signalled. -->
				@if (numberStepCanSkipVerification) {
					<button
						(click)="continueWithoutVerification()"
						class="btn btn--quiet"
						data-testid="guided-setup-number-skip-verification"
						type="button"
					>
						{{ I18N.SIP_BTN_SKIP_VERIFICATION | i18n | async }}
					</button>
				}
				<button
					#numberConfirm
					(click)="confirmNumber()"
					[disabled]="!canFinish"
					class="btn btn--brand"
					data-testid="guided-setup-number-confirm"
					type="button"
				>
					{{
						(store.busy()
							? I18N.SIP_BTN_CHECKING_NUMBER
							: numberStepContinues || offersVerification
								? I18N.SIP_BTN_CONTINUE
								: I18N.SIP_BTN_SAVE_AND_CALL
						)
							| i18n
							| async
					}}
				</button>
			}
			@case ('order') {
				@if (!isLinkEntry) {
					<button
						(click)="dismiss()"
						[disabled]="store.busy()"
						class="btn btn--text"
						data-testid="guided-setup-cancel"
						type="button"
					>
						{{ I18N.SIP_BTN_CANCEL | i18n | async }}
					</button>
				}
				@if (canGoBack) {
					<button
						(click)="back()"
						[disabled]="store.busy()"
						class="btn btn--quiet"
						data-testid="guided-setup-back"
						type="button"
					>
						{{ I18N.SIP_BTN_BACK | i18n | async }}
					</button>
				}
				<button
					(click)="order()"
					[disabled]="!canOrder"
					class="btn btn--brand"
					data-testid="guided-setup-order-confirm"
					type="button"
				>
					{{ (store.busy() ? I18N.SIP_BTN_ORDERING_BUSY : I18N.SIP_BTN_CONFIRM_ORDER) | i18n | async }}
				</button>
			}
			@case ('verify') {
				<button (click)="editNumber()" [disabled]="store.busy()" class="btn btn--text" data-testid="guided-setup-verify-edit" type="button">
					{{ I18N.SIP_BTN_EDIT_NUMBER | i18n | async }}
				</button>
				<button (click)="skipVerification()" [disabled]="store.busy()" class="btn btn--quiet" data-testid="guided-setup-verify-skip" type="button">
					{{ I18N.SIP_BTN_SKIP_VERIFICATION | i18n | async }}
				</button>
				<!-- Replaced by its busy label while the request is out, so a second press cannot ring twice (AC5). -->
				<button
					(click)="triggerVerification()"
					[disabled]="store.busy()"
					class="btn btn--brand"
					data-testid="guided-setup-verify-call"
					type="button"
				>
					{{ (verifyTriggerBusy ? I18N.SIP_BTN_VERIFY_BUSY : I18N.SIP_BTN_VERIFY_CALL) | i18n | async }}
				</button>
			}
			@case ('verifyRunning') {
				<button (click)="editNumber()" [disabled]="store.busy()" class="btn btn--text" data-testid="guided-setup-verify-edit" type="button">
					{{ I18N.SIP_BTN_EDIT_NUMBER | i18n | async }}
				</button>
				<button (click)="skipVerification()" [disabled]="store.busy()" class="btn btn--text" data-testid="guided-setup-verify-skip" type="button">
					{{ I18N.SIP_BTN_SKIP_VERIFICATION | i18n | async }}
				</button>
				<button (click)="cancelVerification()" [disabled]="store.busy()" class="btn btn--quiet" data-testid="guided-setup-verify-cancel" type="button">
					{{ I18N.SIP_BTN_CANCEL_CALL | i18n | async }}
				</button>
			}
			@case ('verified') {
				<!-- The EVA wizards' pattern: continues by itself after a short countdown, or earlier on the button. -->
				@if (autoContinueLabel; as autoContinue) {
					<span class="setup__muted" data-testid="guided-setup-auto-continue">{{ autoContinue }}</span>
				} @else {
					<span></span>
				}
				<button (click)="verification.continueToOverview()" class="btn btn--brand" data-testid="guided-setup-verified-continue" type="button">
					{{ I18N.SIP_BTN_CONTINUE | i18n | async }}
				</button>
			}
			@case ('verifyResult') {
				@if (verifyResultCanEditNumber) {
					<button (click)="editNumber()" class="btn btn--text" data-testid="guided-setup-verify-edit" type="button">
						{{ I18N.SIP_BTN_EDIT_NUMBER | i18n | async }}
					</button>
				}
				@if (verifyResultCanRetry) {
					<button (click)="skipVerification()" class="btn btn--quiet" data-testid="guided-setup-verify-skip" type="button">
						{{ I18N.SIP_BTN_SKIP_VERIFICATION | i18n | async }}
					</button>
					<button (click)="retryVerification()" class="btn btn--brand" data-testid="guided-setup-verify-retry" type="button">
						{{ I18N.SIP_BTN_TRY_AGAIN | i18n | async }}
					</button>
				} @else if (verifyResultIsLocked && !store.manualRequestSent()) {
					<button (click)="skipVerification()" class="btn btn--quiet" data-testid="guided-setup-verify-skip" type="button">
						{{ I18N.SIP_BTN_SKIP_VERIFICATION | i18n | async }}
					</button>
					<button
						(click)="requestManualVerification()"
						[disabled]="store.busy()"
						class="btn btn--brand"
						data-testid="guided-setup-verify-manual"
						type="button"
					>
						{{ I18N.SIP_BTN_REQUEST_MANUAL | i18n | async }}
					</button>
				} @else {
					<!-- Nothing to retry now: carrying on with the 032 number is the way forward, so it is the primary. -->
					<button (click)="skipVerification()" class="btn btn--brand" data-testid="guided-setup-verify-continue" type="button">
						{{ I18N.SIP_BTN_CONTINUE | i18n | async }}
					</button>
				}
			}
			@case ('booked') {
				<!--
					The call is started here, deliberately, instead of firing the moment the booking
					returned. Torsten, 2026-09-04: the booking's answer is the last step, and the customer
					decides when the call goes out.
				-->
				<!--
					`suspend`, NOT `dismiss`. The SIP-Anschluss is booked at this point, and `dismiss`
					resets the run - including `sipOrdered`. With the callnote payload still reporting
					"no SIP-Anschluss" (it is fetched once and never again), the next press of the call
					button walked the customer through a second order confirmation for an account they
					already own; only backend idempotency stood between that and a second paid booking.
					"Later" means come back to this screen, which is what suspend does.
				-->
				<button (click)="suspend()" class="btn btn--text" data-testid="guided-setup-later" type="button">
					{{ I18N.SIP_BTN_LATER | i18n | async }}
				</button>
				<!--
					Until 2026-10-09 this was a du-imperative on a page that is Sie throughout, addressed
					to an AGENT rather than the customer sitting in front of it - on the primary button of
					the screen that follows a paid booking.
				-->
				<button (click)="finish()" class="btn btn--brand" data-testid="guided-setup-call-now" type="button">
					{{ I18N.SIP_BTN_CALL_NOW | i18n | async }}
				</button>
			}
			@case ('rejected') {
				<button (click)="dismiss()" class="btn btn--text" data-testid="guided-setup-rejected-close" type="button">
					{{ I18N.SIP_BTN_NOT_NOW | i18n | async }}
				</button>
				<button
					(click)="endWithServiceContact()"
					class="btn btn--primary"
					data-testid="guided-setup-rejected-contact-service"
					type="button"
				>
					{{ I18N.SIP_BTN_CONTACT_SERVICE | i18n | async }}
				</button>
			}
			@case ('unconfirmed') {
				<button
					(click)="endWithServiceContact()"
					class="btn btn--primary"
					data-testid="guided-setup-unconfirmed-close"
					type="button"
				>
					{{ I18N.SIP_BTN_CLOSE | i18n | async }}
				</button>
			}
			@case ('addressInvalid') {
				<button
					(click)="endWithServiceContact()"
					class="btn btn--primary"
					data-testid="guided-setup-address-invalid-close"
					type="button"
				>
					{{ I18N.SIP_BTN_CLOSE | i18n | async }}
				</button>
			}
			@case ('serviceFollowUp') {
				<button
					(click)="endWithServiceContact()"
					class="btn btn--primary"
					data-testid="guided-setup-service-follow-up-close"
					type="button"
				>
					{{ I18N.SIP_BTN_CLOSE | i18n | async }}
				</button>
			}
			@case ('failed') {
				@if (offersContactService) {
					<button
						(click)="requestServiceContact()"
						class="btn btn--quiet"
						data-testid="guided-setup-contact-service"
						type="button"
					>
						{{ I18N.SIP_BTN_CONTACT_SERVICE | i18n | async }}
					</button>
				}
				<button
					(click)="acknowledgeFailure()"
					class="btn btn--primary"
					data-testid="guided-setup-failed-close"
					type="button"
				>
					{{ I18N.SIP_BTN_CLOSE | i18n | async }}
				</button>
			}
		}
	</footer>
</div>
